export enum ProductComponentType {
  TemplateString = 'template_string',
  Paragraph = 'paragraph',
  LinkButton = 'link_button',
  LinkCard = 'link_card',
  Title = 'title',
  Separator = 'separator',
  Header = 'header',
  Footer = 'footer',
  Card = 'card',
  Collapse = 'collapse',
  HeaderCost = 'header_cost',
  HeaderTitle = 'header_title',
  HeaderImage = 'header_image',
  HeaderAction = 'header_action',
  FooterNextButton = 'footer_next_button',
  UrlInput = 'url_input',
  NumberInput = 'number_input',
  CurrencyInput = 'currency_input',
  CounterInput = 'counter_input',
  TextInput = 'text_input',
  TextAreaInput = 'text_area_input',
  SelectInput = 'select_input',
  RadioInput = 'radio_input',
  BooleanInput = 'boolean_input',
  CheckboxInput = 'checkbox_input',
  DateInput = 'date_input',
  DateRangeInput = 'date_range_input',
  FormField = 'form_field',
  UploadFile = 'upload_file',
  DocumentInput = 'document_input',
  HiddenInput = 'hidden_input',
  List = 'list',
  Icon = 'icon',
  Toast = 'toast',
  Condition = 'condition',

  // layout
  Column = 'column',
  Row = 'row'
}

export enum SizeType {
  Small = 'small',
  Medium = 'medium',
  Large = 'large'
}

export enum SpacingType {
  Small = 'small',
  Medium = 'medium',
  Large = 'large'
}

export interface SpacingProp {
  top?: SpacingType;
  bottom?: SpacingType;
}

export interface WithSpacing {
  spacing?: SpacingType | SpacingProp;
}

export interface ResolvedProp {
  resolvePath: string | string[];
  default: string | number | boolean;
}

export interface ResolvedFormula {
  formula: string;
  inputs: Record<string, string | number | boolean | ResolvedProp>;
}

export interface ExtractPattern {
  regex: string;
  value: string;
}

export interface FormattedDate {
  date: string | { year: number; month: number; day: number };
  format: Intl.DateTimeFormatOptions | 'ISO';
}

export type TemplatedString = TemplateStringProps;

export interface TemplateStringProps {
  template: string;
  values: Record<
    string,
    string | number | ResolvedProp | ResolvedFormula | FormattedDate
  >;
}

export type StringOrTemplateString = string | TemplateString;

export interface ProductBareComponent<TProps = Record<string, unknown>> {
  type: ProductComponentType;
  props?: {
    [K in keyof TProps]: TProps[K] extends string
      ? string | TemplateStringProps | ResolvedProp
      : TProps[K] | ResolvedProp;
  };
  children?: ProductBareComponent | ProductBareComponent[] | string;
}

export interface TemplateString
  extends ProductBareComponent<TemplateStringProps> {
  type: ProductComponentType.TemplateString;
}

export interface ComponentWithSpacing extends ProductBareComponent {
  spacing: SpacingType | SpacingProp;
}

export type BareInputProps = {
  name: string;
  label?: string;
  note?: string;
  placeholder?: string;
  required?: boolean;
};

interface BareInput extends Omit<ProductBareComponent, 'children'> {
  type:
    | ProductComponentType.UrlInput
    | ProductComponentType.NumberInput
    | ProductComponentType.CurrencyInput
    | ProductComponentType.CounterInput
    | ProductComponentType.TextInput
    | ProductComponentType.TextAreaInput
    | ProductComponentType.SelectInput
    | ProductComponentType.RadioInput
    | ProductComponentType.BooleanInput
    | ProductComponentType.DateInput
    | ProductComponentType.DateRangeInput
    | ProductComponentType.DocumentInput
    | ProductComponentType.HiddenInput;
  props: BareInputProps;
}

interface UrlInput extends BareInput {
  type: ProductComponentType.UrlInput;
}

type NumberInputProps = BareInputProps & {
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
};

interface NumberInput extends BareInput {
  type: ProductComponentType.NumberInput;
  props: NumberInputProps;
}

interface CurrencyInput extends Omit<NumberInput, 'type'> {
  type: ProductComponentType.CurrencyInput;
  props: NumberInputProps & {
    currency: 'EUR';
    step?: number;
  };
}

type CounterInputProps = Omit<NumberInputProps, 'label'> & {
  label?: string;
  disabled?: boolean;
  step?: number | number[];
};

interface CounterInput extends Omit<NumberInput, 'type' | 'props'> {
  type: ProductComponentType.CounterInput;
  props: CounterInputProps;
}

type TextInputProps = BareInputProps & {
  defaultValue?: string;
};

interface TextInput extends BareInput {
  type: ProductComponentType.TextInput;
  props: TextInputProps;
}

interface TextAreaInput extends Omit<TextInput, 'type'> {
  type: ProductComponentType.TextAreaInput;
}

type SelectInputProps = BareInputProps & {
  defaultValue?: string | number;
  options: Array<{ value: string | number; text: string }>;
};

interface SelectInput extends BareInput {
  type: ProductComponentType.SelectInput;
  props: SelectInputProps;
}

interface RadioInput extends Omit<SelectInput, 'type'> {
  type: ProductComponentType.RadioInput;
}

interface BooleanInput extends BareInput {
  type: ProductComponentType.BooleanInput;
  props: BareInputProps & {
    defaultValue?: boolean;
  };
}

type DateInputProps = BareInputProps & {
  defaultValue?: Date;
  min?: Date;
  max?: Date;
  inputSize?: 'small' | 'medium' | 'big';
};

interface DateInput extends BareInput {
  type: ProductComponentType.DateInput;
  props: DateInputProps;
}

type DateRangeInputProps = Omit<BareInputProps, 'name' | 'label'> & {
  label?: string;
  defaultValue?: Date;
  min?: Date;
  max?: Date;
  inputSize?: 'small' | 'medium' | 'big';
  readOnly?: boolean;
  dateFromName: string;
  dateToName: string;
};

interface DateRangeInput extends Omit<BareInput, 'props'> {
  type: ProductComponentType.DateRangeInput;
  props: DateRangeInputProps;
}

type DocumentInputProps = {
  name: string;
  required?: boolean;
};

interface DocumentInput extends Omit<BareInput, 'props'> {
  type: ProductComponentType.DocumentInput;
  props: DocumentInputProps;
}

interface HiddenInput extends Omit<BareInput, 'type'> {
  type: ProductComponentType.HiddenInput;
}

type Input =
  | UrlInput
  | NumberInput
  | CurrencyInput
  | TextInput
  | TextAreaInput
  | SelectInput
  | RadioInput
  | BooleanInput
  | DateInput
  | DateRangeInput
  | DocumentInput
  | HiddenInput;

interface Separator extends ProductBareComponent {
  type: ProductComponentType.Separator;
}

interface Paragraph extends ProductBareComponent, WithSpacing {
  type: ProductComponentType.Paragraph;
  children: StringOrTemplateString;
}

interface LinkButtonProps {
  href: string | TemplatedString;
  variant?: 'primary' | 'secondary';
  size?: SizeType;
  fullWidth?: boolean;
  contentLayout?: JustifyContent;
  openInNewTab?: boolean;
}

interface LinkButton
  extends ProductBareComponent<LinkButtonProps>,
    WithSpacing {
  type: ProductComponentType.LinkButton;
  children: string;
}

interface LinkCard extends ProductBareComponent, WithSpacing {
  type: ProductComponentType.LinkCard;
  props: {
    href: string;
    image?: string;
    icon?: string;
    emoji?: string;
    allowTextWrap?: boolean;
    fullWidth?: boolean;
    openInNewTab?: boolean;
    contentLayout?: JustifyContent;
  };
  children: string;
}

export type AlignItems = 'center' | 'start' | 'end' | 'stretch' | 'baseline';
export type JustifyContent =
  | 'center'
  | 'start'
  | 'end'
  | 'stretch'
  | 'space-between';

interface Row extends ProductBareComponent, WithSpacing {
  type: ProductComponentType.Row;
  children: ProductComponent[];
  props: {
    alignItems?: AlignItems;
    justifyContent?: JustifyContent;
  };
}

interface Column extends ProductBareComponent, WithSpacing {
  type: ProductComponentType.Column;
  children: ProductComponent[];
  props: {
    alignItems?: AlignItems;
    justifyContent?: JustifyContent;
  };
}

interface Title extends ProductBareComponent, WithSpacing {
  type: ProductComponentType.Title;
  props: {
    variant: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  };
  children: string;
}

interface HeaderImage extends ProductBareComponent {
  type: ProductComponentType.HeaderImage;
  props?: {
    src?: string;
    position?: 'left' | 'center' | 'right';
  };
}

interface HeaderTitle extends ProductBareComponent, WithSpacing {
  type: ProductComponentType.HeaderTitle;
  props?: {
    position?: 'left' | 'center' | 'right';
  };
}

interface HeaderAction extends ProductBareComponent {
  type: ProductComponentType.HeaderAction;
  props: {
    position: 'left' | 'center' | 'right';
  };
}

interface HeaderCost extends ProductBareComponent {
  type: ProductComponentType.HeaderCost;
  props: {
    amount: 0;
  };
}

interface Header extends ProductBareComponent {
  type: ProductComponentType.Header;
  children: Array<
    ProductComponent | HeaderImage | HeaderTitle | HeaderAction | HeaderCost
  >;
}

interface NextButton {
  type: ProductComponentType.FooterNextButton;
  props: {
    text: string;
  };
}

interface Card extends WithSpacing {
  type: ProductComponentType.Card;
  children: ProductComponent[];
}

interface CollapseProps {
  title: string;
  openedByDefault?: boolean;
}

interface Collapse extends ProductBareComponent<CollapseProps> {
  type: ProductComponentType.Collapse;
  children: ProductComponent[];
}

interface Footer extends Omit<ProductBareComponent, 'props' | 'children'> {
  type: ProductComponentType.Footer;
  children: Array<ProductComponent | NextButton>;
}

interface List {
  type: ProductComponentType.List;
  children: ProductComponent[];
}

interface FormFieldProps {
  label?: string;
  labelSize?: 'small' | 'medium' | 'big';
  required?: boolean;
  error?: string;
  maxWidth?: string;
}

interface FormField extends ProductBareComponent<FormFieldProps>, WithSpacing {
  type: ProductComponentType.FormField;
  children: ProductComponent[];
}

type ConditionProps = {
  value: string | number | boolean;
  eq: string | number | boolean;
  ne: string | number | boolean;
};

interface Condition extends ProductBareComponent<ConditionProps> {
  type: ProductComponentType.Condition;
  children: ProductComponent[];
  props: ConditionProps;
}

export type ProductComponent =
  | Input
  | Paragraph
  | LinkButton
  | LinkCard
  | Title
  | Separator
  | Header
  | Footer
  | Card
  | Collapse
  | Column
  | Row
  | List
  | FormField
  | CounterInput
  | Condition;
